import React, { useState } from 'react';
import { Package, MapPin, Tag, BarChart3, Printer, QrCode, Download, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Item, ItemStatus } from '@/types';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { QRCodeSVG } from 'qrcode.react';
import SimpleLabelPrinter from './SimpleLabelPrinter';


interface ItemDetailModalProps {
  item: Item | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (item: Item) => void;
  onDelete: (item: Item) => void;
}

const ItemDetailModal: React.FC<ItemDetailModalProps> = ({
  item,
  isOpen,
  onClose,
}) => {
  const [showLabelPrinter, setShowLabelPrinter] = useState(false);

  if (!item) return null;

  const checkoutUrl = `${window.location.origin}/checkout/${item.id}`;

  // QR-Code als PNG herunterladen
  const handleDownloadQR = () => {
    // Erstelle ein Canvas-Element für die QR-Code-Konvertierung
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // QR-Code Größe
    const size = 300;
    canvas.width = size;
    canvas.height = size;
    
    // Verwende die QRCode Bibliothek programmatisch
    import('qrcode').then((QRCode) => {
      QRCode.toCanvas(canvas, checkoutUrl, {
        width: size,
        margin: 0, // Kein Rand - QR-Code nutzt volle Größe
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      }, (error) => {
        if (error) {
          console.error('QR-Code Generierung fehlgeschlagen:', error);
          return;
        }

        // Canvas zu Blob konvertieren und herunterladen
        canvas.toBlob((blob) => {
          if (!blob) return;
          
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `qr-code-${item.name.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 'image/png');
      });
    }).catch((error) => {
      console.error('QRCode Bibliothek konnte nicht geladen werden:', error);
    });
  };

  // Checkout-Link in neuem Tab öffnen
  const handleOpenCheckout = () => {
    window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
  };

  const getStatusBadge = (status: ItemStatus) => {
    const statusConfig = {
      [ItemStatus.AVAILABLE]: { variant: 'default' as const, label: 'Verfügbar' },
      [ItemStatus.CHECKED_OUT]: { variant: 'secondary' as const, label: 'Ausgeliehen' },
      [ItemStatus.MAINTENANCE]: { variant: 'destructive' as const, label: 'Wartung' },
      [ItemStatus.RETIRED]: { variant: 'outline' as const, label: 'Außer Betrieb' },
    };

    const config = statusConfig[status];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const renderAttributes = () => {
    if (!item.attributes) return null;

    return (
      <div className="space-y-2">
        <h4 className="font-medium text-sm">Attribute:</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          {Object.entries(item.attributes).map(([key, value]) => (
            <div key={key} className="flex justify-between">
              <span className="text-muted-foreground capitalize">{key}:</span>
              <span className="font-medium">
                {Array.isArray(value) ? value.join(', ') : String(value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {item.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <h3 className="font-semibold text-lg">{item.name}</h3>
                {item.description && (
                  <p className="text-muted-foreground">{item.description}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    <span className="text-muted-foreground">Mat. Nr:</span> {item.sku || '-'}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    <span className="text-muted-foreground">Status:</span> {getStatusBadge(item.status)}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    <span className="text-muted-foreground">Standort:</span> {item.location?.name || '-'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-medium mb-2">Bestand</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Aktuell:</span>
                    <span className="font-medium">{item.quantity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Minimum:</span>
                    <span className="font-medium">{item.minQuantity}</span>
                  </div>
                  {item.isRental && (
                    <div className="flex justify-between">
                      <span>Typ:</span>
                      <span className="font-medium text-blue-600">Mietgerät</span>
                    </div>
                  )}
                  {item.serialNumber && (
                    <div className="flex justify-between">
                      <span>Seriennummer:</span>
                      <span className="font-medium">{item.serialNumber}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-xs text-muted-foreground">
                <div>Erstellt: {format(new Date(item.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}</div>
                {item.updatedAt && (
                  <div>Aktualisiert: {format(new Date(item.updatedAt), 'dd.MM.yyyy HH:mm', { locale: de })}</div>
                )}
              </div>
            </div>
          </div>

          {/* Attributes */}
          {item.attributes && renderAttributes()}

          {/* QR Code Section */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <QrCode className="h-4 w-4" />
              QR-Code
            </h4>
            <div className="flex items-center justify-center bg-white p-4 rounded-lg border">
              <QRCodeSVG
                value={checkoutUrl}
                size={150}
                level="M"
                includeMargin={true}
              />
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Scannen Sie den QR-Code für die Direktausleihe
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-2 pt-4 border-t">
            <Button
              onClick={() => setShowLabelPrinter(true)}
              className="flex items-center gap-2"
            >
              <Printer className="h-4 w-4" />
              Label drucken
            </Button>
            
            <Button
              variant="outline"
              onClick={handleDownloadQR}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              QR-Code PNG
            </Button>
            
            <Button
              variant="outline"
              onClick={handleOpenCheckout}
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Checkout öffnen
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Label Printer Modal */}
    {showLabelPrinter && (
      <SimpleLabelPrinter
        item={item}
        isOpen={showLabelPrinter}
        onClose={() => setShowLabelPrinter(false)}
      />
    )}
    </>
  );
};

export default ItemDetailModal;
